import { BUTTON_SIZE, BUTTON_TYPE, DSButton, useToast } from '@repo/design-system/index'
import { QIcon_chevron_left } from '@repo/icons/index'
import { ActivityComponentType, useActivity } from '@stackflow/react'
import { QueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import apis from '../../../modules/apis'
import { enterConversationAi } from '../../../modules/apis/student/conversation'
import { axiosGet, axiosPost } from '../../../modules/axiosHelper'
import { useCurrentUser } from '../../../modules/context/UserContext'
import useAppRouter from '../../../modules/hooks/common/useAppRouter'
import useReturnFullUrl from '../../../modules/hooks/webview/common/useReturnFullUrl'
import useWebviewNavigation from '../../../modules/hooks/webview/common/useWebviewNavigation'
import { n_material } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import { MaterialSubTab, TextProvider, fetchMaterial } from '../../../pages/[lang]/portal/materials/detail/[materialId]'
import { useLessonsStack } from '../../../pages/[lang]/webview/lessons'
import MaterialBodyComponent from '../../new-portal/materials/MaterialBodyComponent'
import MaterialMobileBottomTab from '../../new-portal/materials/MaterialMobileBottomTab'
import WebviewStackContainer from '../common/WebviewStackContainer'
import { ConversationLessonStartButton } from '../materials/MaterialsDetailActivity'

interface IMaterialDetail {
  params: {
    materialId: string
    lessonId: any
  }
}

export const MaterialDetailBar = ({ pop, lessonId, materialId }) => {
  const { t } = useTranslation()
  const { toastError } = useToast()
  const [loading, setLoading] = useState(false)
  const { popBackToRingleApp } = useWebviewNavigation()
  const activity = useActivity()

  const handleSelectMaterial = async (lessonId, materalId) => {
    try {
      if (!lessonId) {
        toastError('수업이 할당되지 않았습니다.')
        return
      }

      setLoading(true)
      const response = await axiosPost(apis.lessons.editCourse(), {
        lesson_id: lessonId,
        course_id: materalId,
      })

      if (response.success) {
        popBackToRingleApp()
      }
    } catch (e) {
      setLoading(false)
      alert(e.message)
    }
  }

  return (
    <div className="flex w-screen flex-row items-center justify-between mx-[16px]">
      <div
        className="mr-auto "
        onClick={() => {
          if (activity.isRoot) {
            popBackToRingleApp()
          } else {
            pop()
          }
        }}
      >
        <QIcon_chevron_left className="h-24 w-24 text-black" />
      </div>
      {lessonId && lessonId !== 'undefined' && (
        <div className="">
          <DSButton
            isLoading={loading}
            size={BUTTON_SIZE.SM}
            type={BUTTON_TYPE.SOLID}
            onClick={() => handleSelectMaterial(lessonId, materialId)}
          >
            <DSButton.Label>{t(n_material.select)}</DSButton.Label>
          </DSButton>
        </div>
      )}
    </div>
  )
}

const LessonMaterialDetailActivity: ActivityComponentType = ({ params }: IMaterialDetail) => {
  const { useLessonsFlow } = useLessonsStack()
  const { pop } = useLessonsFlow()
  const { currentUser } = useCurrentUser()
  const router = useAppRouter()
  const getFullUrl = useReturnFullUrl()

  const [activeTabIdx, setActiveTabIdx] = useState<number>(0)
  const [materialData, setMaterialData] = useState(null)

  // Scroll and Show Top/Bottom Nav
  const [showNav, setShowNav] = useState(true)
  // AI Speaking Partner
  const [isConversationAvailable, setIsConversationAvailable] = useState<boolean | { [x: string]: boolean }>(true)
  const [loading, setLoading] = useState(false)

  const getMaterailData = async () => {
    const queryClient = new QueryClient()

    const [_, json] = await Promise.all([
      queryClient.prefetchQuery(['material', params.materialId], () => fetchMaterial(params.materialId)),
      axiosGet(apis.course.detail({ id: params.materialId })),
    ])

    if (json.success) {
      setMaterialData(json)
    }
  }

  const onStartConversation = async (courseId) => {
    if (loading) return
    setLoading(true)
    await enterConversationAi({ courseId }, router, getFullUrl, true)
    setLoading(false)
  }

  useEffect(() => {
    getMaterailData()
  }, [])

  useEffect(() => {
    if (!materialData) return
    if (!currentUser) return
    setIsConversationAvailable(!materialData.course.is_speaking && !materialData.course.is_writing && !!currentUser)
  }, [materialData, currentUser])
  return (
    <TextProvider>
      <WebviewStackContainer
        title={<MaterialDetailBar pop={pop} lessonId={params?.lessonId} materialId={params.materialId} />}
      >
        <div className="min-h-screen">
          <MaterialSubTab activeTabIdx={activeTabIdx} setActiveTabIdx={setActiveTabIdx} />
          {materialData && (
            <div className="relative mt-[20px] h-screen">
              <MaterialBodyComponent
                activeTabIdx={activeTabIdx}
                materialData={materialData}
                materialId={params.materialId}
                setShowNav={setShowNav}
              />
            </div>
          )}
          {materialData && isConversationAvailable && showNav && (
            <ConversationLessonStartButton
              onStartConversation={() => onStartConversation(params.materialId)}
              materialId={params.materialId}
            />
          )}
        </div>
      </WebviewStackContainer>
      {materialData && <MaterialMobileBottomTab course={materialData?.course} />}
    </TextProvider>
  )
}

export default LessonMaterialDetailActivity
